import { useState, useEffect, useMemo, useRef } from 'react';
import { Layout, Menu, Button, Space, Typography, Dropdown, Modal, Input, List, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  ShoppingOutlined,
  DatabaseOutlined,
  UploadOutlined,
  UnorderedListOutlined,
  UserOutlined,
  BarChartOutlined,
  UserSwitchOutlined,
  DownOutlined,
  PlusOutlined,
  SettingOutlined,
  MessageOutlined,
  ImportOutlined,
  ExportOutlined,
  ShopOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  MoonOutlined,
  SunOutlined,
  CloudUploadOutlined,
  DashboardOutlined,
  LineChartOutlined,
  BookOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounce } from '@/hooks/useDebounce';
import { useTheme } from '@/contexts/ThemeContext';
import { useQuery } from '@apollo/client/react';
import { MERCHANTS_QUERY, MerchantsResponse, MerchantListItem } from '@/graphql/queries/merchants';
import { apolloClientV2 } from '@/lib/apollo-client-v2';
import { Notifications } from '@/components/Notifications/Notifications';
import { NewVersionBanner } from '@/components/NewVersionBanner';
import { colors } from '@/lib/antd-config';
import './SidebarLayout.css';

// Build time formatter - shows local time
const formatBuildTime = (): string => {
  // Access build-time constants injected by Vite (try both window and import.meta.env)
  const buildTime = (window as any).__BUILD_TIME__ || import.meta.env.VITE_BUILD_TIME;
  const buildTimestamp = (window as any).__BUILD_TIMESTAMP__ || import.meta.env.VITE_BUILD_TIMESTAMP;
  
  if (!buildTime && !buildTimestamp) {
    return 'dev';
  }
  
  try {
    const buildDate = buildTimestamp 
      ? new Date(Number(buildTimestamp))
      : new Date(buildTime);
    
    // Format as: MM/DD h:mm AM/PM (local time, 12-hour)
    const month = String(buildDate.getMonth() + 1).padStart(2, '0');
    const day = String(buildDate.getDate()).padStart(2, '0');
    const hours = buildDate.getHours();
    const displayHours = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const minutes = String(buildDate.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${displayHours}:${minutes} ${ampm}`;
  } catch {
    return buildTime || 'unknown';
  }
};

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

export function SidebarLayout() {
  // Initialize collapsed state from localStorage, default to true
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved !== null ? saved === 'true' : true;
  });
  const [isImpersonationModalVisible, setIsImpersonationModalVisible] = useState(false);
  const [merchantSearchQuery, setMerchantSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(merchantSearchQuery, 300);
  const [showLoginNotifications, setShowLoginNotifications] = useState(false);
  const [loginNotificationHandledFor, setLoginNotificationHandledFor] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [openKeysPath, setOpenKeysPath] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { selectedMerchant, setSelectedMerchant } = useImpersonation();
  const { hasPermission } = usePermissions();
  // Merchant-scoped features (e.g. Address Book) require an active merchant
  // context: a merchant assigned to the account, or an impersonated merchant.
  const hasMerchantContext = !!user?.profile?.merchantId || !!selectedMerchant;
  const { themeMode, toggleTheme } = useTheme();
  const searchInputRef = useRef<any>(null);

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const userKey = user ? String(user.id ?? user.username) : null;

  if (
    userKey &&
    sessionStorage.getItem('justLoggedIn') === 'true' &&
    loginNotificationHandledFor !== userKey
  ) {
    setLoginNotificationHandledFor(userKey);
    setShowLoginNotifications(true);
    sessionStorage.removeItem('justLoggedIn');
  }

  const { data: merchantsData, loading: merchantsLoading, error: merchantsError } = useQuery<MerchantsResponse>(MERCHANTS_QUERY, {
    variables: { 
      active: true,
      search: debouncedSearchQuery.trim() || null,
      page: 1,
      pageSize: 200, // Get enough merchants for search
    },
    skip: !isImpersonationModalVisible, // Only fetch when modal is open
    fetchPolicy: 'network-only', // Always fetch fresh data when modal opens
    client: apolloClientV2,
  });


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleImpersonationClick = () => {
    setIsImpersonationModalVisible(true);
  };

  const handleMerchantSelect = (merchant: MerchantListItem) => {
    setSelectedMerchant(merchant);
    setIsImpersonationModalVisible(false);
    setMerchantSearchQuery(''); // Reset search when closing
  };

  const handleImpersonationModalCancel = () => {
    setIsImpersonationModalVisible(false);
    setMerchantSearchQuery(''); // Reset search when closing
  };

  const handleEndImpersonation = () => {
    setSelectedMerchant(null);
  };

  // Use merchants from query (backend handles search filtering)
  const filteredMerchants = useMemo(() => {
    if (!merchantsData?.merchants?.edges) return [];
    return merchantsData.merchants.edges.filter((m): m is MerchantListItem => m !== null);
  }, [merchantsData]);

  const menuItems = useMemo(() => {
    const ordersChildren = [
      ...(hasPermission('orders.actions.view') ? [{
        key: '/orders',
        icon: <UnorderedListOutlined />,
        label: 'View Orders',
      }] : []),
      ...(hasPermission('orders.actions.edit') ? [{
        key: '/orders/create',
        icon: <PlusOutlined />,
        label: 'Create Order',
      }] : []),
      ...(hasPermission('orders.actions.csvUpload') ? [{
        key: '/orders/csv-upload',
        icon: <UploadOutlined />,
        label: 'Upload CSV',
      }] : []),
      ...((hasPermission('addressBook.actions.view') && hasMerchantContext) ? [{
        key: '/orders/address-book',
        icon: <BookOutlined />,
        label: 'Address Book',
      }] : []),
    ];

    return [
      ...(hasPermission('system.dashboard.view') ? [{
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
      }] : []),
      ...(ordersChildren.length > 0 ? [{
        key: 'orders-menu',
        icon: <ShoppingOutlined />,
        label: 'Orders',
        children: ordersChildren,
      }] : []),
    ...(() => {
      const inventoryChildren = [
        ...(hasPermission('inventory.actions.viewHealth') ? [{
          key: '/inventory/health',
          icon: <LineChartOutlined />,
          label: 'Inventory Health',
        }] : []),
        ...(hasPermission('inventory.actions.view') ? [{
          key: '/inventory',
          icon: <DatabaseOutlined />,
          label: 'Products',
        }] : []),
        ...(hasPermission('inventory.actions.skuTranslations.view') ? [{
          key: '/inventory/sku-translations',
          icon: <UnorderedListOutlined />,
          label: 'Translations',
        }] : []),
        ...(hasPermission('inventory.actions.viewInbound') ? [{
          key: '/inventory/inbounds',
          icon: <ImportOutlined />,
          label: 'Inbounds',
        }] : []),
        ...(hasPermission('inventory.actions.viewOutbound') || hasPermission('inventory.actions.createOutbound') ? [{
          key: '/inventory/outbounds',
          icon: <ExportOutlined />,
          label: 'Outbounds',
        }] : []),
        ...(hasPermission('inventory.actions.viewLots') ? [{
          key: '/inventory/lots',
          icon: <UnorderedListOutlined />,
          label: 'Lots',
        }] : []),
        ...(hasPermission('inventory.actions.viewCycleCount') ? [{
          key: '/inventory/cycle-count',
          icon: <UnorderedListOutlined />,
          label: 'Cycle Count',
        }] : []),
        ...(hasPermission('inventory.actions.view') ? [{
          key: '/inventory/warehouse',
          icon: <DatabaseOutlined />,
          label: 'Warehouse Inventory',
        }] : []),
        ...(hasPermission('lotRecall.actions.view') ? [{
          key: '/inventory/lot-recall',
          icon: <SearchOutlined />,
          label: 'Lot Recall',
        }] : []),
      ];
      return inventoryChildren.length > 0 ? [{
        key: 'inventory-menu',
        icon: <DatabaseOutlined />,
        label: 'Inventory',
        children: inventoryChildren,
      }] : [];
    })(),
    ...((hasPermission('reports.actions.view') || hasPermission('reports.actions.performance.view')) ? [{
      key: 'reports-menu',
      icon: <BarChartOutlined />,
      label: 'Reports',
      children: [
        ...(hasPermission('reports.actions.view') ? [
          { key: '/reports/orders', icon: <UnorderedListOutlined />, label: 'Orders' },
          { key: '/reports/custom-orders', icon: <UnorderedListOutlined />, label: 'Custom Orders' },
          { key: '/reports/shipping', icon: <UnorderedListOutlined />, label: 'Shipping' },
          { key: '/reports/inventory-activity', icon: <UnorderedListOutlined />, label: 'Inventory Activity' },
          { key: '/reports/product-volume', icon: <UnorderedListOutlined />, label: 'Product Volume' },
          { key: '/reports/monthly-volume', icon: <UnorderedListOutlined />, label: 'Monthly Volume' },
          { key: '/reports/sales-samples', icon: <UnorderedListOutlined />, label: 'Sales Samples' },
          { key: '/reports/warehouse-lots', icon: <UnorderedListOutlined />, label: 'Warehouse Lots' },
          { key: '/reports/warehouse-inventory', icon: <UnorderedListOutlined />, label: 'Warehouse Inventory' },
          { key: '/reports/packing', icon: <UnorderedListOutlined />, label: 'Packing' },
        ] : []),
        ...(hasPermission('reports.actions.performance.view') ? [
          { key: '/reports/grievance-summary', icon: <BarChartOutlined />, label: 'Grievance Summary' },
          { key: '/reports/carrier-performance', icon: <BarChartOutlined />, label: 'Carrier Performance' },
        ] : []),
      ],
    }] : []),
    ...(hasPermission('billing.actions.view') ? [{
      key: 'billing-menu',
      icon: <DollarOutlined />,
      label: 'Billing',
      children: [
        {
          key: '/billing/events',
          icon: <UnorderedListOutlined />,
          label: 'Events',
        },
        {
          key: '/billing/runs',
          icon: <DashboardOutlined />,
          label: 'Billing Runs',
        },
        {
          key: '/billing/bills',
          icon: <DollarOutlined />,
          label: 'Bills',
        },
        {
          key: '/billing/failures',
          icon: <ImportOutlined />,
          label: 'Failures',
        },
      ],
    }] : []),
    ...(hasPermission('merchants.actions.view') ? [{
      key: '/merchants',
      icon: <ShopOutlined />,
      label: 'Merchants',
    }] : []),
    ...(hasPermission('system.loadouts.view') || hasPermission('system.loadouts.edit') || hasPermission('system.packaging.view') || hasPermission('system.packaging.edit') || hasPermission('system.perishability.view') || hasPermission('system.perishability.edit') ? [{
      key: 'packaging-menu',
      icon: <DatabaseOutlined />,
      label: 'Packaging',
      children: [
        ...(hasPermission('system.loadouts.view') || hasPermission('system.loadouts.edit') ? [{
          key: '/packaging/loadouts',
          icon: <UnorderedListOutlined />,
          label: 'Loadouts',
        }] : []),
        ...(hasPermission('system.packaging.view') || hasPermission('system.packaging.edit') ? [{
          key: '/packaging/boxes-coolers',
          icon: <DatabaseOutlined />,
          label: 'Boxes / Coolers',
        }, {
          key: '/packaging/optimizer',
          icon: <AppstoreOutlined />,
          label: 'Optimizer',
        }] : []),
        ...(hasPermission('system.perishability.view') || hasPermission('system.perishability.edit') ? [{
          key: '/packaging/perishability',
          icon: <UnorderedListOutlined />,
          label: 'Perishability',
        }] : []),
      ],
    }] : []),
    ...(hasPermission('system.messages.view') || hasPermission('system.messages.edit') || hasPermission('users.actions.manageUsers') || hasPermission('system.holidayCalendar.view') || hasPermission('system.holidayCalendar.edit') || hasPermission('assets.view') || hasPermission('assets.upload') ? [{
      key: 'system-menu',
      icon: <SettingOutlined />,
      label: 'System',
      children: [
        ...(hasPermission('users.actions.manageUsers') ? [{
          key: '/system/users',
          icon: <UserOutlined />,
          label: 'Users',
        }] : []),
        ...(hasPermission('system.messages.view') || hasPermission('system.messages.edit') ? [{
          key: '/system/messages',
          icon: <MessageOutlined />,
          label: 'Messages',
        }] : []),
        ...(hasPermission('system.holidayCalendar.view') || hasPermission('system.holidayCalendar.edit') ? [{
          key: '/system/holiday-calendar',
          icon: <CalendarOutlined />,
          label: 'Holidays',
        }] : []),
        ...(hasPermission('assets.view') || hasPermission('assets.upload') ? [{
          key: '/system/assets',
          icon: <CloudUploadOutlined />,
          label: 'Assets',
        }] : []),
      ],
    }] : []),
    ];
  }, [hasPermission, hasMerchantContext]);

  const handleMenuClick = ({ key }: { key: string }) => {
    // Find the clicked item to check if it has children
    const findItem = (items: typeof menuItems, targetKey: string): any => {
      for (const item of items) {
        if (item.key === targetKey) {
          return item;
        }
        if (item.children) {
          const found = findItem(item.children, targetKey);
          if (found) return found;
        }
      }
      return null;
    };
    
    const clickedItem = findItem(menuItems, key);
    
    // Only navigate if it's a leaf item (no children or empty children array)
    // Parent items with children will be handled by Ant Design's onOpenChange
    if (clickedItem && (!clickedItem.children || clickedItem.children.length === 0)) {
      navigate(key);
    }
  };

  const selectedKeys = useMemo(() => {
    const pathname = location.pathname;
    const allKeys: string[] = [];
    const traverse = (nodes: typeof menuItems) => {
      nodes.forEach((node) => {
        allKeys.push(node.key);
        if (node.children) traverse(node.children);
      });
    };
    traverse(menuItems);

    const matchingKey = allKeys
      .filter((key) => pathname === key || pathname.startsWith(`${key}/`))
      .sort((a, b) => b.length - a.length)[0];

    return matchingKey ? [matchingKey] : [];
  }, [location.pathname, menuItems]);

  const pathBasedOpenKeys = useMemo(() => {
    const keys: string[] = [];
    const pathname = location.pathname;
    if (pathname.startsWith('/orders')) keys.push('orders-menu');
    if (pathname.startsWith('/inventory')) keys.push('inventory-menu');
    if (pathname.startsWith('/reports')) keys.push('reports-menu');
    if (pathname.startsWith('/packaging')) keys.push('packaging-menu');
    if (pathname.startsWith('/system')) keys.push('system-menu');
    if (pathname.startsWith('/billing')) keys.push('billing-menu');
    return keys;
  }, [location.pathname]);

  if (!collapsed && openKeysPath !== location.pathname) {
    setOpenKeysPath(location.pathname);
    setOpenKeys(pathBasedOpenKeys);
  }

  // Focus search input when modal opens
  useEffect(() => {
    if (isImpersonationModalVisible && searchInputRef.current) {
      // Small delay to ensure modal animation completes
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isImpersonationModalVisible]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        collapsedWidth={80}
        breakpoint="lg"
        onBreakpoint={(broken) => {
          // On mobile (broken = true), collapse the sidebar
          // On desktop (broken = false), restore saved state from localStorage
          if (broken) {
            setCollapsed(true);
          } else {
            // On desktop, restore saved state
            const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
            if (saved !== null) {
              setCollapsed(saved === 'true');
            }
          }
        }}
        style={{
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        className="app-sidebar"
      >
        <div className="logo" style={{ padding: '12px 16px', textAlign: 'center', flexShrink: 0 }}>
          <Text strong style={{ color: '#fff', fontSize: collapsed ? '14px' : '16px' }}>
            {collapsed 
              ? (user?.isStaff ? 'MA' : 'MC')
              : (user?.isStaff ? 'Merchant Admin' : 'Merchant Center')
            }
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          inlineIndent={0}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={(keys) => {
            const rootSubMenuKeys = ['orders-menu', 'inventory-menu', 'reports-menu', 'packaging-menu', 'system-menu', 'billing-menu'];
            const latestOpenKey = keys.find(key => !openKeys.includes(key));
            if (latestOpenKey && rootSubMenuKeys.includes(latestOpenKey)) {
              setOpenKeys([latestOpenKey]);
            } else {
              setOpenKeys(keys);
            }
          }}
          items={menuItems}
          onClick={handleMenuClick}
          triggerSubMenuAction="click"
          style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
        />
        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          flexDirection: 'column',
          gap: '8px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          flexShrink: 0,
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: colors.sidebarBg
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              color: '#fff',
              width: '100%',
            }}
          />
          {/* Build version indicator - visible in production for deployment verification */}
          {!collapsed && (
            <div style={{ 
              fontSize: '10px', 
              color: 'rgba(255, 255, 255, 0.5)',
              textAlign: 'center',
              lineHeight: '1.4',
              paddingTop: '4px',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            }}>
              <div>Build: {formatBuildTime()}</div>
              <div style={{ fontSize: '9px', marginTop: '2px' }}>
                v{(window as any).__BUILD_VERSION__ || import.meta.env.VITE_BUILD_VERSION || 'dev'}
              </div>
            </div>
          )}
        </div>
      </Sider>
      {!collapsed && (
        <div
          className="sidebar-backdrop"
          onClick={() => setCollapsed(true)}
          aria-hidden="true"
        />
      )}
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'margin-left 0.2s' }} className="main-layout">
        <Header
          style={{
            padding: '0 24px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            background: colors.sidebarBg,
            color: 'rgba(255, 255, 255, 0.85)',
            position: 'fixed',
            top: 0,
            right: 0,
            left: collapsed ? 80 : 240,
            zIndex: 100,
            transition: 'left 0.2s',
          }}
          className="app-header"
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.85)',
              display: 'none',
            }}
            className="mobile-menu-trigger"
          />
          {user && (
            <Space size="small" wrap className="header-actions">
              <Button
                type="text"
                icon={themeMode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
                title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{ color: 'rgba(255, 255, 255, 0.85)' }}
              />
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Notifications onLogin={showLoginNotifications} />
              </div>
              <Dropdown
                menu={{
                  items: [
                    ...(hasPermission('actions.impersonation') || selectedMerchant ? [
                      {
                        key: selectedMerchant ? 'endImpersonation' : 'impersonation',
                        icon: <UserSwitchOutlined />,
                        label: selectedMerchant ? 'End Impersonation' : 'Impersonation',
                        onClick: selectedMerchant ? handleEndImpersonation : handleImpersonationClick,
                      },
                    {
                      type: 'divider' as const,
                    },
                    ] : []),
                    {
                      key: 'logout',
                      icon: <LogoutOutlined />,
                      label: 'Logout',
                      danger: true,
                      onClick: handleLogout,
                    },
                  ],
                }}
                placement="bottomRight"
              >
                <Button type="text" style={{ padding: '4px 8px', height: 'auto', minHeight: '44px', color: 'rgba(255, 255, 255, 0.85)' }} className="user-menu-button">
                  <Space size="small">
                    <UserOutlined style={{ color: 'rgba(255, 255, 255, 0.85)' }} />
                    <Text style={{ color: 'rgba(255, 255, 255, 0.85)' }} className="user-name-text">
                      {user.firstName} {user.lastName}
                      {selectedMerchant && ` (${selectedMerchant.name})`}
                    </Text>
                    <DownOutlined style={{ fontSize: '10px', opacity: 0.65, color: 'rgba(255, 255, 255, 0.85)' }} />
                  </Space>
                </Button>
              </Dropdown>
            </Space>
          )}
        </Header>
        <Content
          style={{
            marginTop: 64, // Account for fixed header height
            padding: 8,
            minHeight: 280,
            background: colors.bgBase,
            width: '100%',
            maxWidth: 'none',
          }}
          className="main-content"
        >
          <NewVersionBanner />
          <Outlet />
        </Content>
      </Layout>

      <Modal
        title="Impersonate Merchant"
        open={isImpersonationModalVisible}
        onCancel={handleImpersonationModalCancel}
        footer={null}
        width={500}
        className="impersonation-modal"
        styles={{
          body: {
            padding: '16px 0',
          },
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            ref={searchInputRef}
            placeholder="Search merchants by name or code..."
            prefix={<SearchOutlined />}
            value={merchantSearchQuery}
            onChange={(e) => setMerchantSearchQuery(e.target.value)}
            size="large"
            autoFocus
            allowClear
          />
        </div>

        {merchantsError && (
          <div style={{ marginBottom: 16 }}>
            <Typography.Text type="danger">
              Error loading merchants: {merchantsError.message}
            </Typography.Text>
          </div>
        )}

        {merchantsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Typography.Text type="secondary">Loading merchants...</Typography.Text>
          </div>
        ) : filteredMerchants.length === 0 ? (
          <Empty
            description={
              merchantSearchQuery
                ? `No merchants found matching "${merchantSearchQuery}"`
                : 'No merchants available'
            }
            style={{ padding: '40px 0' }}
          />
        ) : (
          <List
            dataSource={filteredMerchants}
            renderItem={(merchant) => (
              <List.Item
                style={{
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.highlightBg;
                  e.currentTarget.style.border = '1px solid #91d5ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.border = '1px solid transparent';
                }}
                onClick={() => handleMerchantSelect(merchant)}
              >
                <Typography.Text strong style={{ fontSize: '15px' }}>
                  {merchant.name}
                </Typography.Text>
              </List.Item>
            )}
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          />
        )}

        {filteredMerchants.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.borderLight}` }}>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Showing {filteredMerchants.length} of {merchantsData?.merchants?.edges?.length || 0} merchant{filteredMerchants.length !== 1 ? 's' : ''}
            </Typography.Text>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

